/**
 * Renderer and scene setup.
 *
 * Lighting is deliberately minimal: ONE directional light casting shadows with a
 * tight orthographic frustum that follows the player, plus a hemisphere fill.
 * Everything else is baked into vertex color or texture.
 *
 * DO NOT ADD MORE SHADOW CASTERS. It is the single most likely change to tank
 * mobile frame rate, and with 3-band toon shading the visual gain is close to
 * zero. See AGENTS.md's do-not list.
 */

import * as THREE from 'three';
import type { QualityTierSettings } from '@/core/tuning';
import { tuning } from '@/core/tuning';

export interface RendererBundle {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  sun: THREE.DirectionalLight;
  ambient: THREE.HemisphereLight;
}

export function createRenderer(
  canvas: HTMLCanvasElement,
  settings: QualityTierSettings,
): RendererBundle {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: settings.antialias,
    powerPreference: 'high-performance',
    // The game always fills the viewport, so we never need alpha compositing.
    alpha: false,
    stencil: false,
  });

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, settings.pixelRatioCap));
  renderer.setSize(window.innerWidth, window.innerHeight, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.shadowMap.enabled = settings.shadows;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color('#f6c99f');
  // A little fog to keep distant rooms from reading as flat cutouts. Kept very
  // subtle - heavy fog fights the saturated palette.
  scene.fog = new THREE.Fog('#f6c99f', 18, 42);

  const camera = new THREE.PerspectiveCamera(
    tuning.camera.fovDeg,
    window.innerWidth / window.innerHeight,
    0.05,
    120,
  );
  camera.position.set(0, 3, 6);

  // Warm afternoon sun through a window. The angle is chosen so cat and granny
  // both cast readable shadows toward the camera, which helps the player judge
  // heights when pouncing onto furniture.
  const sun = new THREE.DirectionalLight('#fff2d8', 2.1);
  sun.position.set(5, 9, 4);
  sun.castShadow = settings.shadows;
  configureSunShadow(sun, settings.shadowMapSize);
  scene.add(sun);
  scene.add(sun.target);

  // Sky/ground bounce. Cheap, and it stops the shadow side of objects from
  // going muddy - important because the toon ramp's dark band is quite light.
  const ambient = new THREE.HemisphereLight('#cfe4ff', '#e0a878', 1.15);
  scene.add(ambient);

  return { renderer, scene, camera, sun, ambient };
}

/**
 * Tight ortho frustum around the play area. A cascade-free single shadow map is
 * plenty for room-scale interiors and costs a fraction of what CSM would.
 */
function configureSunShadow(sun: THREE.DirectionalLight, mapSize: number): void {
  if (mapSize <= 0) {
    sun.castShadow = false;
    return;
  }
  sun.shadow.mapSize.set(mapSize, mapSize);
  const extent = 8;
  sun.shadow.camera.left = -extent;
  sun.shadow.camera.right = extent;
  sun.shadow.camera.top = extent;
  sun.shadow.camera.bottom = -extent;
  sun.shadow.camera.near = 0.5;
  sun.shadow.camera.far = 30;
  // Bias tuned for our scale (a 0.34m-tall cat). Too high and small props
  // detach from their shadows; too low and you get acne on the floor.
  sun.shadow.bias = -0.0006;
  sun.shadow.normalBias = 0.02;
  sun.shadow.camera.updateProjectionMatrix();
}

/** Keeps the shadow frustum centered on the player so it stays tight. */
export function followSunTarget(sun: THREE.DirectionalLight, target: THREE.Vector3): void {
  sun.target.position.copy(target);
  sun.position.set(target.x + 5, target.y + 9, target.z + 4);
  sun.target.updateMatrixWorld();
}

export function applyQualitySettings(bundle: RendererBundle, settings: QualityTierSettings): void {
  bundle.renderer.setPixelRatio(Math.min(window.devicePixelRatio, settings.pixelRatioCap));
  bundle.renderer.shadowMap.enabled = settings.shadows;
  bundle.sun.castShadow = settings.shadows;
  configureSunShadow(bundle.sun, settings.shadowMapSize);
}

export function handleResize(bundle: RendererBundle): void {
  const width = window.innerWidth;
  const height = window.innerHeight;
  bundle.renderer.setSize(width, height, false);
  bundle.camera.aspect = width / height;
  bundle.camera.updateProjectionMatrix();
}

/** Full teardown. Called on hot reload so WebGL contexts do not leak. */
export function disposeRenderer(bundle: RendererBundle): void {
  bundle.scene.traverse((node) => {
    if (node instanceof THREE.Mesh) {
      node.geometry.dispose();
      const materials = Array.isArray(node.material) ? node.material : [node.material];
      for (const material of materials) material.dispose();
    }
  });
  bundle.renderer.dispose();
}
